import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getToken } from '../../utils/auth';
import {
  XMarkIcon,
  ArrowPathIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';

const { VITE_API_SERVICE } = import.meta.env;

/**
 * Makes an authenticated API call to the specified endpoint
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} The parsed response data
 */
async function apiCall(endpoint, options = {}) {
  const token = getToken();
  const response = await fetch(`${VITE_API_SERVICE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(
      data.message || `Failed to ${options.method || 'fetch'} data`
    );
  }
  return data;
}

/**
 * Fetches user data from the API
 * @param {string} userId - The ID of the user to fetch
 * @returns {Promise<Object>} The user data
 */
async function fetchUserData(userId) {
  const data = await apiCall('/v1/users/get', {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
  return data.user;
}

/**
 * Fetches all publishers from the API
 * @returns {Promise<Object>} List of publishers
 */
async function fetchPublishers() {
  return apiCall('/v1/publishers/list');
}

/**
 * Updates the publishers assigned to a user
 * @param {string} userId - The ID of the user to update
 * @param {Array} publishers - Array of publisher objects
 * @returns {Promise<Object>} Update response
 */
async function updateUserPublishers(userId, publishers) {
  return apiCall('/v1/users/update', {
    method: 'POST',
    body: JSON.stringify({ userId, publishers })
  });
}

/**
 * Normalizes a publisher object to a consistent format
 * @param {(Object|number)} publisher - Publisher object or ID
 * @param {Object} allPublishers - Object containing all publishers
 * @returns {Object|null} Normalized publisher object or null if not found
 */
function normalizePublisher(publisher, allPublishers) {
  const id = Number(publisher.id || publisher);
  if (typeof publisher === 'number') {
    const found = allPublishers.publishers.find(function (p) {
      return Number(p.id) === id;
    });
    return found ? { id, name: found.name } : null;
  }
  return { id, name: publisher.name };
}

export default function PublisherAssignment() {
  const { userId } = useParams();

  // State
  const [publisherSearch, setPublisherSearch] = useState('');
  const [selectedPublishers, setSelectedPublishers] = useState(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedExpanded, setSelectedExpanded] = useState(true);
  const [availableExpanded, setAvailableExpanded] = useState(true);

  // Queries
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: async function () {
      if (!userId) return null;
      return fetchUserData(userId);
    },
    enabled: !!userId,
    retry: 2,
    staleTime: 30000
  });

  const {
    data: allPublishers,
    isLoading: isLoadingAllPublishers,
    error: allPublishersError,
    refetch: refetchAllPublishers
  } = useQuery({
    queryKey: ['all-publishers'],
    queryFn: async function () {
      return fetchPublishers();
    },
    staleTime: 60000,
    retry: 2
  });

  // Mutations
  const updatePublishersMutation = useMutation({
    mutationFn: function (newPublishers) {
      const publisherData = Array.from(newPublishers.values());
      return updateUserPublishers(userId, publisherData);
    },
    onSuccess: async function () {
      setHasUnsavedChanges(false);
      await Promise.all([refetchUser(), refetchAllPublishers()]);
    }
  });

  // Effects
  useEffect(
    function () {
      if (userData?.publishers && allPublishers?.publishers) {
        initializeSelectedPublishers(userData, allPublishers);
      }
    },
    [userData, allPublishers]
  );

  useEffect(
    function () {
      setHasUnsavedChanges(false);
    },
    [userId]
  );

  useEffect(function () {
    return function () {
      debouncedSetSearch.cancel();
    };
  }, []);

  /**
   * Initializes the selected publishers map from user data
   * @param {Object} userData - The user data containing publishers
   * @param {Object} allPublishers - Object containing all publishers
   */
  function initializeSelectedPublishers(userData, allPublishers) {
    const publisherMap = new Map();
    const publisherObjects = Array.isArray(userData.publishers)
      ? userData.publishers
      : allPublishers.publishers.filter(function (p) {
          return userData.publishers.includes(Number(p.id));
        });

    publisherObjects.forEach(function (pub) {
      const normalized = normalizePublisher(pub, allPublishers);
      if (normalized) {
        publisherMap.set(normalized.id, normalized);
      }
    });
    setSelectedPublishers(publisherMap);
  }

  /**
   * Returns filtered publishers based on search term
   * @returns {Array} Filtered publishers array
   */
  function getFilteredPublishers() {
    const filteredPublishers = allPublishers?.publishers || [];
    return publisherSearch
      ? filteredPublishers.filter(function (publisher) {
          return publisher?.name
            ?.toLowerCase()
            .includes(publisherSearch.toLowerCase());
        })
      : filteredPublishers;
  }

  /**
   * Splits publishers into selected and available arrays
   * @param {Array} publishers - Array of all publishers
   * @returns {Object} Object containing selected and available publishers
   */
  function splitPublishers(publishers) {
    const selectedIds = new Set(Array.from(selectedPublishers.keys()));
    return {
      selected: publishers.filter(function (publisher) {
        return selectedIds.has(Number(publisher.id));
      }),
      available: publishers.filter(function (publisher) {
        return !selectedIds.has(Number(publisher.id));
      })
    };
  }

  /**
   * Handles toggling publisher selection
   * @param {Object} publisher - The publisher to toggle
   */
  function handlePublisherChange(publisher) {
    const normalized = normalizePublisher(publisher, {
      publishers: [publisher]
    });
    if (!normalized) return;

    setSelectedPublishers(function (prev) {
      const newMap = new Map(prev);
      if (newMap.has(normalized.id)) {
        newMap.delete(normalized.id);
      } else {
        newMap.set(normalized.id, normalized);
      }
      return newMap;
    });
    setHasUnsavedChanges(true);
  }

  /**
   * Handles saving publisher assignments
   */
  function handleSave() {
    updatePublishersMutation.mutate(selectedPublishers);
  }

  const debouncedSetSearch = debounce(function (value) {
    setPublisherSearch(value);
  }, 300);

  /**
   * Renders the publisher search box
   * @returns {JSX.Element} Search box component
   */
  function renderSearchBox() {
    return (
      <div className="form-control mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search publishers..."
            className="input input-bordered input-sm w-full pr-10"
            value={publisherSearch}
            onChange={function (e) {
              setPublisherSearch(e.target.value);
              debouncedSetSearch(e.target.value);
            }}
          />
          {publisherSearch && (
            <button
              className="btn btn-ghost btn-xs absolute right-2 top-1/2 -translate-y-1/2"
              onClick={function () {
                setPublisherSearch('');
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // UI Components
  const renderPublisherList = (publishers, isSelected) => (
    <div className="space-y-1 mt-2">
      {publishers.map((publisher) => (
        <label
          key={publisher.id}
          className={`flex items-center p-2 gap-3 rounded-lg ${
            isSelected ? 'bg-base-200 hover:bg-base-300' : 'hover:bg-base-200'
          } cursor-pointer transition-colors`}
        >
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={isSelected}
            onChange={() => handlePublisherChange(publisher)}
          />
          <span className="text-sm">{publisher.name}</span>
        </label>
      ))}
    </div>
  );

  const renderPublisherSection = (
    title,
    publishers,
    isExpanded,
    setExpanded,
    isSelected
  ) => (
    <div>
      <button
        onClick={() => setExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 hover:bg-base-200 rounded-lg transition-colors"
      >
        <h3 className="text-sm font-medium opacity-70">
          {title} ({publishers.length})
        </h3>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
        />
      </button>
      {isExpanded && renderPublisherList(publishers, isSelected)}
    </div>
  );

  // Main Render
  const isLoading = isLoadingUser || isLoadingAllPublishers;
  const error = userError || allPublishersError;
  const publishers = getFilteredPublishers();
  const { selected, available } = splitPublishers(publishers);

  return (
    <div className="card bg-white border border-base-200">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h2 className="card-title text-lg">Publisher Assignment</h2>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2">
              <span className="badge badge-warning badge-sm">Unsaved</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSave}
                disabled={updatePublishersMutation.isPending}
              >
                {updatePublishersMutation.isPending ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          )}
        </div>

        {userId ? (
          <>
            <div className="text-sm font-medium mb-4">
              Assigning publishers to:{' '}
              <span className="text-base-content">{userData?.email}</span>
            </div>
            {renderSearchBox()}
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-40">
                  <span className="loading loading-spinner loading-md"></span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-sm text-error mb-2">
                    {(userError || allPublishersError).message}
                  </p>
                  <button
                    onClick={() => {
                      refetchUser();
                      refetchAllPublishers();
                    }}
                    className="btn btn-ghost btn-xs gap-2"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  {selected.length > 0 &&
                    renderPublisherSection(
                      'Selected Publishers',
                      selected,
                      selectedExpanded,
                      setSelectedExpanded,
                      true
                    )}
                  {available.length > 0 &&
                    renderPublisherSection(
                      'Available Publishers',
                      available,
                      availableExpanded,
                      setAvailableExpanded,
                      false
                    )}
                  {publishers.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-sm opacity-60">
                        {publisherSearch
                          ? `No publishers found matching "${publisherSearch}"`
                          : 'No publishers available'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm opacity-60 text-center py-4">
            Select a user to assign publishers
          </div>
        )}
      </div>
    </div>
  );
}
